// CResultPointScene — 牌局結束畫面 2／3：算台數
// 對應反編譯原版 GameResultPointScene。
//
// 內容：左上 winner 的 header（頭像+座位字+風字+名字）；下方台數明細（3 欄格狀，
// 「台型 N 台」）；暖金分隔線；「共N台」總計；街機「繼續」鈕 → ResultMoney。
//
// 正式牌局採順位攔胡，因此只會有一位 winner。
// 流局：不會進本 scene（由 ResultTile 直接跳 ResultMoney）。
//
// 預覽：?scene=ResultPoint（無 payload → 注入 mock）。
//
// === 使用圖片 ===
// - 桌背景 + 遮罩 + header 圖 + 繼續鈕：見 CBaseResultScene 檔頭
// - 標題與明細由 Text 繪製，不使用對話框；繼續鈕見 CBaseResultScene。

import { reveal, animateTo, reducedMotion, Motion } from '@ui/motion';
import { LOGICAL_WIDTH, SCREEN_CENTER_X } from '@config/game-config';
import type Phaser from 'phaser';
import { RoundSummaryKind } from '@core/summary';
import type { THuResult } from '@core/summary';
import { ZH_TW } from '@i18n/zh-tw';
import { FONT_STYLES } from '@ui/index';
import { createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { playUIClick } from '@audio/scene-audio';

import { CBaseResultScene } from './base-result-scene';
import { RESULT_BTN_CONTINUE_KEY } from './boot-scene';
import { SceneKey } from './scene-keys';
import type { TResultFlowData } from './result-flow-data';

// ============================================================================
// 版面常數
// ============================================================================
const ITEM_STAGGER_MS = 35; // 台數列錯開
const ITEM_STAGGER_MAX_MS = 100; // 明細多時仍在 300ms 內完成
const TOTAL_FROM_SCALE = 0.94; // 總台數輕彈起始比例
const HEADER_X = 130; // winner header 左上 X
const HEADER_Y = 153; // winner header 中心 Y
const HEADER_SCALE = 0.8; // 頭像列縮放

// ---- 台數明細格狀 ----
const POINTS_BASE_Y = 230; // 第一列明細 Y
const POINTS_COLUMNS = 3; // 欄數
const POINTS_ROW_STEP = 60; // 列距（上界；台型過多時自動壓縮）
const POINTS_BOTTOM_GAP = 24; // 最後一列距黃線的最小留白（壓縮列距的下界依據）
const POINTS_FONT_SIZE = '30px'; // 明細字體
const POINTS_COLOR = PAGE_THEME.text;

// ---- 分隔線 / 總計 / 按鈕 ----
const LINE_Y = 452; // 黃線 Y
const LINE_COLOR = PAGE_THEME.gold; // 黃線色
const LINE_THICKNESS = 1; // 細金分隔線
const LINE_WIDTH_RATIO = 0.78; // 線寬占畫面比例
const TOTAL_Y = 520; // 共N台 Y
const TOTAL_FONT_SIZE = '52px'; // 共N台字體
const TOTAL_COLOR = PAGE_THEME.goldText;
const BUTTON_Y = 642; // 繼續鈕 Y

const TITLE_Y = 94; // 共用頁標題
const POINTS_INSET = 100; // 明細左右留白

export class CResultPointScene extends CBaseResultScene {
  private continueButton!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: SceneKey.ResultPoint });
  }

  init(data?: TResultFlowData): void {
    this.resolveFlow(data);
  }

  create(): void {
    this.createBackdrop();
    createPageTitle(this, SCREEN_CENTER_X, TITLE_Y, '台數明細');

    // 防呆：流局沒有 winner（正常不會走到這）→ 直接進籌碼
    if (this.flow.summary.kind !== RoundSummaryKind.Hu || this.flow.summary.results.length === 0) {
      this.scene.start(SceneKey.ResultMoney, this.flow);
      return;
    }

    const result = this.flow.summary.results[0] as THuResult;

    this.renderWinnerHeader(result);
    this.renderPointItems(result);
    this.renderLine();
    this.renderTotal(result);
    this.continueButton = this.renderContinueButton();
    this.setResultButtonFocused(this.continueButton, true);
    this.continueButton.on('pointerout', () => {
      this.setResultButtonFocused(this.continueButton, true);
    });
    this.input.keyboard?.on('keydown', this.handleKeyboard, this);
    this.events.once('shutdown', () =>
      this.input.keyboard?.off('keydown', this.handleKeyboard, this)
    );
  }

  // --------------------------------------------------------------------------
  private renderWinnerHeader(result: THuResult): void {
    const winner = this.flow.players[result.seat];
    if (!winner) return;
    this.renderPlayerHeader(HEADER_X, HEADER_Y, winner, HEADER_SCALE);
  }

  /** 台數明細：3 欄格狀，「台型 N 台」。 */
  private renderPointItems(result: THuResult): void {
    const colWidth = (LOGICAL_WIDTH - POINTS_INSET * 2) / POINTS_COLUMNS;
    // 台型過多時壓縮列距，避免明細列越過黃線、蓋到「共N台」總計
    const rowCount = Math.ceil(result.scoreItems.length / POINTS_COLUMNS);
    const available = LINE_Y - POINTS_BOTTOM_GAP - POINTS_BASE_Y;
    const rowStep =
      rowCount > 0 ? Math.min(POINTS_ROW_STEP, available / rowCount) : POINTS_ROW_STEP;
    result.scoreItems.forEach((item, i) => {
      const label = ZH_TW.scoring[item.key as keyof typeof ZH_TW.scoring] ?? item.key;
      const col = i % POINTS_COLUMNS;
      const row = Math.floor(i / POINTS_COLUMNS);
      const cx = POINTS_INSET + colWidth * col + colWidth / 2;
      const y = POINTS_BASE_Y + row * rowStep;
      const text = this.add
        .text(cx, y, `${label} ${item.points} 台`, {
          ...FONT_STYLES.result,
          fontFamily: PAGE_THEME.font,
          fontSize: POINTS_FONT_SIZE,
          color: POINTS_COLOR,
        })
        .setOrigin(0.5);
      reveal(this, text, Math.min(row * ITEM_STAGGER_MS, ITEM_STAGGER_MAX_MS));
    });
  }

  private renderLine(): void {
    const w = LOGICAL_WIDTH * LINE_WIDTH_RATIO;
    this.add.rectangle(SCREEN_CENTER_X, LINE_Y, w, LINE_THICKNESS, LINE_COLOR).setOrigin(0.5);
  }

  private renderTotal(result: THuResult): void {
    const text = this.add
      .text(SCREEN_CENTER_X, TOTAL_Y, `共${result.total}台`, {
        ...FONT_STYLES.title,
        fontFamily: PAGE_THEME.font,
        fontSize: TOTAL_FONT_SIZE,
        color: TOTAL_COLOR,
      })
      .setOrigin(0.5);
    const rowCount = Math.ceil(result.scoreItems.length / POINTS_COLUMNS);
    const delay =
      Motion.enterMs + Math.min(Math.max(0, rowCount - 1) * ITEM_STAGGER_MS, ITEM_STAGGER_MAX_MS);
    if (!reducedMotion()) text.setScale(TOTAL_FROM_SCALE).setAlpha(0);
    animateTo(
      this,
      text,
      { scaleX: 1, scaleY: 1, alpha: 1 },
      Motion.popMs,
      'Back.easeOut',
      () => playUIClick(this),
      delay
    );
  }

  private renderContinueButton(): Phaser.GameObjects.Container {
    return this.createResultButton(SCREEN_CENTER_X, BUTTON_Y, RESULT_BTN_CONTINUE_KEY, () => {
      this.scene.start(SceneKey.ResultMoney, this.flow);
    });
  }

  /** 原 APP 單一焦點按鈕：Enter／Space／遙控器確認鍵皆等同點擊繼續。 */
  private handleKeyboard(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      playUIClick(this);
      this.activateResultButton(this.continueButton);
    }
  }
}
